import React from 'react'

import { Card, makeAppStyles, Money, Typography } from '@percept/mui'

import { PerformanceDetailCard, PerformanceDetailCardContent } from './PerformanceDetailCard'

import { getComparisonFunction } from '@percept/utils'

import { providerLabelMap } from '@percept/constants'

import { ReportProviderV2 } from '@percept/types'

import { OrgUnitPerformanceDatum } from './typings'


export type SingleOrgUnitPerformanceCardProps = {
  currency?: string
  provider: ReportProviderV2
  segment: string | null
  metric: string
} & OrgUnitPerformanceDatum

const useStyles = makeAppStyles( theme => ({
  collatedCard: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    padding: theme.spacing(1, 3),
  },
  collatedCardTitle: {
    height: '100%',
  },
  collatedCardContent: {
    padding: theme.spacing(1, 0),
    marginRight: theme.spacing(8),
    '&:last-of-type': {
      marginRight: theme.spacing(0),
    }
  },
  collatedGridContainer: {
    display: 'flex',
    flexGrow: 1,
    flexWrap: 'wrap',
  },
  collatedGridItem: {
    display: 'flex',
    paddingBottom: 'unset',
    paddingTop: 'unset',
    marginRight: theme.spacing(8),
    '&:last-of-type': {
      marginRight: theme.spacing(0),
    }
  },
  content: {
    display: 'flex',
    justifyContent: 'space-evenly',
  },
  value: {
    userSelect: 'none',
    pointerEvents: 'none',
    display: 'flex',
    alignItems: 'center',
    fontSize: 26,
    margin: theme.spacing(1, 0),
    fontWeight: theme.typography.fontWeightBold,
    [theme.breakpoints.down('md')]: {
      fontSize: 20,
    },
  },
  notApplicable: {
    color: theme.palette.text.disabled,
  },
  orgLink: {
    display: 'flex',
    alignItems: 'center',
  },
  icon: {
    marginLeft: theme.spacing(0.5),
    color: 'inherit',
  },
  linkIcon: {
    marginRight: theme.spacing(2),
  },
  changeValueNegative: {
    fontSize: 18,
    marginLeft: theme.spacing(1.5),
    color: theme.chart.healthColourScale(0),
    [theme.breakpoints.down('md')]: {
      fontSize: 14,
    },
  },
  changeValuePositive: {
    fontSize: 18,
    marginLeft: theme.spacing(1.5),
    color: theme.chart.healthColourScale(1),
    [theme.breakpoints.down('md')]: {
      fontSize: 14,
    },
  },
}))


const comparisonFunction = getComparisonFunction('ABSOLUTE')

export const SingleOrgUnitPerformanceCard = ({
  provider,
  currency,
  segment,
  metric,
  currentValue,
  previousValue,
}: SingleOrgUnitPerformanceCardProps) => {
  const classes = useStyles()
  const change = comparisonFunction(currentValue, previousValue)
  const changeClassName = (
    Number(change) > 0 ?
      classes.changeValueNegative :
      classes.changeValuePositive
  )
  return (
    <PerformanceDetailCard
      provider={provider}
      segment={segment}
      metric={metric}>
      <div className={classes.content}>
        { currentValue !== null && (
          <div className={classes.value}>
            <Money amount={currentValue} currency={currency} />
            { change !== null && (
              <span className={changeClassName}>
                <Money
                  amount={change}
                  currency={currency}
                  forceRenderSign />
              </span>
            )}
          </div>
        )}
      </div>
    </PerformanceDetailCard>
  )
}


export type SingleOrgUnitPerformanceCardCollatedProps = (
  Pick<SingleOrgUnitPerformanceCardProps, 'currency' | 'provider'>
  & {
    data: (
      Omit<SingleOrgUnitPerformanceCardProps, 'currency' | 'provider'>
    )[]
  }
)

export const SingleOrgUnitPerformanceCardCollated = ({
  provider,
  currency,
  data,
}: SingleOrgUnitPerformanceCardCollatedProps) => {
  const classes = useStyles()
  return (
    <Card
      className={classes.collatedCard}
      elevation={3}>
      <Typography variant='h5'>
        { providerLabelMap[provider] }
      </Typography>
      <div className={classes.collatedGridContainer}>
        { data.map( ({ segment, metric, currentValue, previousValue }) => {
          const change = comparisonFunction(currentValue, previousValue)
          const changeClassName = (
            Number(change) > 0 ?
              classes.changeValueNegative :
              classes.changeValuePositive
          )
          return (
            <div
              key={segment || metric}
              className={classes.collatedGridItem}>
              <PerformanceDetailCardContent
                className={classes.collatedCardContent}
                title={segment}
                subtitle={metric}>
                <div className={classes.content}>
                  <div className={classes.value}>
                    { currentValue !== null && (
                      <>
                        <Money amount={currentValue} currency={currency} />
                        { change !== null && (
                          <span className={changeClassName}>
                            <Money
                              amount={change}
                              currency={currency}
                              forceRenderSign />
                          </span>
                        )}
                      </>
                    ) || (
                      <span className={classes.notApplicable}>
                        N / A
                      </span>
                    )}
                  </div>
                </div>
              </PerformanceDetailCardContent>
            </div>
          )
        })}
      </div>
    </Card>
  )
}
